import React, { useCallback } from 'react'

import { Locale, checkLocale } from '@toasttab/buffet-pui-locale-utilities'
import {
  formatCurrency,
  getCurrencySymbol,
  Currency
} from '@toasttab/buffet-pui-number-utilities'

import { Money } from 'src/apollo/onlineOrdering'
import {
  useOptionalRestaurant,
  useRestaurant
} from 'src/shared/components/common/restaurant_context/RestaurantContext'

export type I18nType = {
  currency: Currency
  locale: Locale
  country?: string | null
}

export function useFormatPriceCallback() {
  const { ooRestaurant } = useRestaurant()
  const restaurantI18n = ooRestaurant?.i18n as I18nType | undefined
  const currency = restaurantI18n?.currency || Currency.USD
  // Use narrowSymbol for AUD to show "$" instead of "A$"
  const currencyDisplay = currency === Currency.AUD ? 'narrowSymbol' : 'symbol'
  return useCallback(
    (amount: number, trailingZeros?: boolean) => {
      return formatCurrency(
        { amount, currency },
        checkLocale(restaurantI18n?.locale),
        { trailingZeros, currencyDisplay }
      )
    },
    [currency, currencyDisplay, restaurantI18n?.locale]
  )
}

export function useFormatPrice(price: number, trailingZeros?: boolean) {
  const formatPriceCallback = useFormatPriceCallback()
  return formatPriceCallback(price, trailingZeros)
}

/**
 * Formats to a string from the "Money" type in defined on the Federated Gateway
 */
export function useFormatMoney(): (money: Money) => string {
  const ooRestaurant = useOptionalRestaurant()?.ooRestaurant
  const restaurantI18n: I18nType | undefined = ooRestaurant?.i18n as
    | I18nType
    | undefined
  return React.useCallback(
    (money: Pick<Money, 'amount' | 'currency'>) => {
      return formatCurrency(
        {
          amount: money.amount,
          currency: `${money.currency}` as Currency
        },
        checkLocale(restaurantI18n?.locale)
      )
    },
    [restaurantI18n?.locale]
  )
}

export function useCurrencySymbol(): string {
  const { ooRestaurant } = useRestaurant()
  const restaurantI18n: I18nType | undefined = ooRestaurant?.i18n as
    | I18nType
    | undefined

  const currency = restaurantI18n?.currency || Currency.USD
  return getCurrencySymbol(currency, checkLocale(restaurantI18n?.locale)) ?? '$'
}

export const roundPrice = (price: number) =>
  Math.round((price + Number.EPSILON) * 100) / 100
